/**
 * UI strings for the Taiken calendar.
 *
 * Japanese is the default. English is complete and switchable, ready for the
 * planned English site; no UI string is hardcoded in the components.
 */

export const DEFAULT_LOCALE = 'ja';

export const STRINGS = {
    ja: {
        overline: 'KURKKU FIELDS 体験',
        heading: '体験カレンダー',
        dayView: '日別',
        monthlyView: '月別',
        sunnyPlan: '晴れの日',
        rainyPlan: '雨の日',
        searchPlaceholder: '体験名・内容で検索',
        clearSearch: '検索条件をクリア',
        categoryAll: 'すべて',
        targetAll: 'すべての方',
        target_family_kids: 'ファミリー・お子さま',
        target_couples_adults: 'カップル・大人',
        selectedDate: '日付',
        month: '月',
        gridCalendar: 'カレンダー',
        listView: '一覧',
        timelineFor: '{date} の体験',
        noEventsInHour: '予定なし',
        noEventsToday: 'この条件に合う体験はありません。条件を変えてお試しください。',
        ongoing: '継続中',
        untilTime: '{time} まで',
        details: '詳細を見る',
        close: '閉じる',
        eventCount: '{count}件',
        weekdays: ['日', '月', '火', '水', '木', '金', '土'],
        labelDays: '開催日',
        labelTime: '時間',
        labelPrice: '料金',
        labelCapacity: '定員',
        labelBooking: '受付',
        colEvent: '体験',
        colDays: '開催日',
        colWeather: '天候',
        colTime: '時間・定員',
        colBooking: '料金・受付',
        weather_all_weather: '天候を問わず',
        weather_sunny_only: '晴天時のみ',
        weather_rainy_only: '雨天時のみ',
        days_weekday: '平日',
        days_weekend_holiday: '土日・祝日',
        days_both: '平日・土日祝',
        days_specific: '指定日のみ',
        openDay: '{date} の体験を見る',
        switchToEnglish: 'English',
        switchToJapanese: '日本語',
        holiday: '祝日',
        byDayType: '平日 {weekday} ／ 土日祝 {weekend}',
        closed: '休園日',
        otherDates: '開催日',
        labelRange: '開催期間',
        runsUntil: '{start} 〜 {end}',
        moreDates: 'ほか{count}日',
        closedToday: 'この日は休園日です。',
    },
    en: {
        overline: 'KURKKU FIELDS Experiences',
        heading: 'Experience Calendar',
        dayView: 'Day',
        monthlyView: 'Month',
        sunnyPlan: 'Sunny day',
        rainyPlan: 'Rainy day',
        searchPlaceholder: 'Search experiences',
        clearSearch: 'Clear search',
        categoryAll: 'All',
        targetAll: 'Everyone',
        target_family_kids: 'Families & kids',
        target_couples_adults: 'Couples & adults',
        selectedDate: 'Date',
        month: 'Month',
        gridCalendar: 'Calendar',
        listView: 'List',
        timelineFor: 'Experiences on {date}',
        noEventsInHour: 'Nothing scheduled',
        noEventsToday: 'No experiences match these filters. Try adjusting them.',
        ongoing: 'Ongoing',
        untilTime: 'until {time}',
        details: 'View details',
        close: 'Close',
        eventCount: '{count} events',
        weekdays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        labelDays: 'Days',
        labelTime: 'Time',
        labelPrice: 'Price',
        labelCapacity: 'Capacity',
        labelBooking: 'Booking',
        colEvent: 'Experience',
        colDays: 'Days',
        colWeather: 'Weather',
        colTime: 'Time & capacity',
        colBooking: 'Price & booking',
        weather_all_weather: 'All weather',
        weather_sunny_only: 'Sunny only',
        weather_rainy_only: 'Rainy only',
        days_weekday: 'Weekdays',
        days_weekend_holiday: 'Weekends & holidays',
        days_both: 'Weekdays, weekends & holidays',
        days_specific: 'Selected dates',
        openDay: 'View experiences on {date}',
        switchToEnglish: 'English',
        switchToJapanese: '日本語',
        holiday: 'Holiday',
        byDayType: 'Weekdays {weekday} / Weekends & holidays {weekend}',
        closed: 'Closed',
        otherDates: 'Dates',
        labelRange: 'Runs',
        runsUntil: '{start} – {end}',
        moreDates: '+{count} more',
        closedToday: 'The park is closed on this day.',
    },
};

/**
 * Build a translator bound to one locale.
 *
 * @param {string} locale 'ja' or 'en'.
 * @returns {(key: string, vars?: Object) => any} Lookup that interpolates {vars}.
 */
export function createTranslator(locale) {
    const table = STRINGS[locale] || STRINGS[DEFAULT_LOCALE];

    return function t(key, vars) {
        const value = table[key] !== undefined ? table[key] : STRINGS[DEFAULT_LOCALE][key];
        if (typeof value !== 'string' || !vars) {
            return value !== undefined ? value : key;
        }
        return value.replace(/\{(\w+)\}/g, (match, name) =>
            (vars[name] !== undefined ? String(vars[name]) : match)
        );
    };
}

/**
 * Pick the locale-appropriate value, falling back to Japanese when an English
 * translation has not been filled in yet.
 *
 * @param {string} locale Active locale.
 * @param {string} ja     Japanese value.
 * @param {string} en     English value, possibly empty.
 * @returns {string}
 */
export function localized(locale, ja, en) {
    if (locale === 'en') {
        return en && String(en).trim() !== '' ? en : ja;
    }
    return ja;
}
